import * as THREE from 'three';
import { materialPool } from './MaterialPool';

// ============================================================
// Smoke trail — dark particles trailing behind damaged aircraft
// ============================================================

const PARTICLE_LIFETIME = 1.5;
const EMIT_RATE = 0.03; // seconds between particles
const PARTICLE_SIZE = 2;
const SMOKE_COLOR = 0x222222;

const smokeGeom = new THREE.SphereGeometry(PARTICLE_SIZE, 4, 4);

interface SmokeParticle {
  readonly mesh: THREE.Mesh;
  readonly velocity: THREE.Vector3;
  age: number;
}

export class SmokeTrail {
  private readonly scene: THREE.Scene;
  private readonly particles: SmokeParticle[] = [];
  private emitTimer = 0;
  private intensity = 0; // 0 = off, 1 = max smoke (near death)

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** Set smoke intensity based on damage: 0 = no smoke, 1 = maximum */
  setIntensity(intensity: number): void {
    this.intensity = Math.max(0, Math.min(1, intensity));
  }

  setActive(active: boolean): void {
    this.intensity = active ? 0.5 : 0;
  }

  update(source: THREE.Object3D, dt: number): void {
    // Emit new particles scaled by intensity
    if (this.intensity > 0) {
      this.emitTimer -= dt;
      // Higher intensity = faster emit rate (more particles)
      const rate = EMIT_RATE / this.intensity;
      if (this.emitTimer <= 0) {
        this.emitTimer = rate;
        this.spawnParticle(source);
      }
    }

    // Update existing particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;

      if (p.age >= PARTICLE_LIFETIME) {
        this.scene.remove(p.mesh);
        materialPool.release(p.mesh.material as THREE.MeshBasicMaterial);
        this.particles.splice(i, 1);
        continue;
      }

      // Drift upward + slow down
      p.mesh.position.addScaledVector(p.velocity, dt);
      p.velocity.multiplyScalar(0.97);
      p.velocity.y += 3 * dt; // rise

      // Grow and fade
      const t = p.age / PARTICLE_LIFETIME;
      const scale = 1 + t * 3;
      p.mesh.scale.setScalar(scale);
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - t);
    }
  }

  private spawnParticle(source: THREE.Object3D): void {
    const mat = materialPool.acquire();
    mat.color.set(SMOKE_COLOR);
    mat.opacity = 0.6;

    const mesh = new THREE.Mesh(smokeGeom, mat);
    mesh.position.copy(source.position);
    mesh.frustumCulled = false;

    // Random spread
    const spread = 2;
    mesh.position.x += (Math.random() - 0.5) * spread;
    mesh.position.y += (Math.random() - 0.5) * spread;
    mesh.position.z += (Math.random() - 0.5) * spread;

    // Inherit some backward velocity from source
    const velocity = new THREE.Vector3(0, 0, 1)
      .applyQuaternion(source.quaternion)
      .multiplyScalar(15)
      .add(new THREE.Vector3(
        (Math.random() - 0.5) * 5,
        Math.random() * 3,
        (Math.random() - 0.5) * 5,
      ));

    this.scene.add(mesh);
    this.particles.push({ mesh, velocity, age: 0 });
  }

  dispose(): void {
    for (const p of this.particles) {
      this.scene.remove(p.mesh);
      materialPool.release(p.mesh.material as THREE.MeshBasicMaterial);
    }
    this.particles.length = 0;
  }
}
